import { Sparkles } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import documentsAnimation from '../../../assets/lottie/documents_scan.json';
import { LottieAnimation } from '../../common/LottieAnimation';

interface AIExtractionLoadingModalProps {
  isOpen: boolean;
  fileName?: string;
}

const STATUS_MESSAGES = [
  'Reading your menu file...',
  'Identifying menu items...',
  'Detecting categories...',
  'Mapping departments...',
  'Extracting prices...',
  'Discovering add-ons...',
  'Almost there...',
];

export const AIExtractionLoadingModal: React.FC<
  AIExtractionLoadingModalProps
> = ({ isOpen, fileName }) => {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setMessageIndex(0);
      return undefined;
    }

    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % STATUS_MESSAGES.length);
    }, 2200);

    return () => clearInterval(interval);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop — intentionally no click handler */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm"
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-sm mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Top accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-pos-primary via-blue-400 to-pos-primary bg-[length:200%_100%] animate-[shimmer_2s_linear_infinite]" />

        <div className="px-8 py-10 flex flex-col items-center text-center">
          {/* Animation */}
          <div className="w-40 h-40 mb-4">
            <LottieAnimation animationData={documentsAnimation} />
          </div>

          {/* Icon + title */}
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={18} className="text-pos-primary" />
            <h3 className="text-lg font-semibold text-gray-900">
              AI is analyzing your menu
            </h3>
          </div>

          {/* Cycling status message */}
          <p
            key={messageIndex}
            className="text-sm text-gray-500 h-5 animate-[fadeInUp_0.4s_ease-out]"
          >
            {STATUS_MESSAGES[messageIndex]}
          </p>

          {/* File name */}
          {fileName && (
            <div className="mt-5 flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-100">
              <div className="w-1.5 h-1.5 rounded-full bg-pos-primary animate-pulse" />
              <span className="text-xs text-gray-500 truncate max-w-[240px]">
                {fileName}
              </span>
            </div>
          )}

          {/* Progress dots */}
          <div className="mt-6 flex items-center gap-1.5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full bg-pos-primary/30 animate-bounce"
                style={{ animationDelay: `${i * 0.18}s` }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
